import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Icon from '../ui/Icon';
import { Toggle, PageSkeleton } from '../ui/primitives';
import { useAdminToast } from './AdminShell';
import { API_URL } from '../config/api';
import { useOrg } from '../contexts/OrgContext';
import { isAgentOnline } from './agentStatus';
import { useSearchParams } from 'react-router-dom';
import StockSettingsTab from './magazyn/StockSettingsTab';

/* ---------- typy ---------- */
type Printer = {
  id?: string; name: string; type?: string; ip?: string; port?: number;
  protocol?: string; format?: string; is_default?: boolean; is_active?: boolean;
  agent_id?: string | null; invert_colors?: boolean;
};
type PrintAgent = { id: string; name: string; status?: string };
/* Zdrowie druku — GET /api/settings/printers/health (watchdog: to, co dziś widać tylko w bazie). */
type PrintingHealth = {
  generatedAt: string;
  stuckJobs: { id: string; documentType: string; printerIp: string | null; createdAt: string; waitingSeconds: number }[];
  agents: { id: string; name: string; lastSeenAt: string | null; online: boolean }[];
  printers: { id: string; name: string; agentId: string | null; agentName: string | null; agentOnline: boolean; issue: 'agent_missing' | 'agent_offline' | null }[];
  duplicateDefaults: string[];
};
type PackageType = { id?: string; name: string; length?: number; width?: number; height?: number; weight?: number; category_ids?: string[]; is_active?: boolean };
type UcsCategory = { id: string; name: string };
/* category_ids kartonu: tablica (Postgres jsonb) lub string JSON lub null → string[]. */
function parseCatIds(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string' && raw.trim()) {
    try { const v = JSON.parse(raw); return Array.isArray(v) ? v.map(String) : []; } catch { return []; }
  }
  return [];
}
type OrgUser = { id?: string; first_name: string; last_name: string; pin?: string; role: string; is_active?: boolean; last_login_at?: string };
type Workstation = { id?: string; station_id: string; name: string; location?: string; description?: string; is_active?: boolean; ui_scale?: number; text_scale?: number };

/** Clamp a kiosk scale percentage to the supported 50–300% range (defaults to 100 on NaN). */
function clampScale(n: number): number {
  if (!Number.isFinite(n)) return 100;
  return Math.min(300, Math.max(50, Math.round(n)));
}
type Sender = { name?: string; firstName?: string; lastName?: string; email?: string; phone?: string; street?: string; building?: string; city?: string; postalCode?: string };
type Device = { id: string; name: string; status: string; last_seen_at?: string; app_version?: string; version_code?: number; platform?: string; model?: string; station_id?: string | null };

/* dhl_label zniknal — etykiety kurierskie przychodza z UCS; goodspeed_label ZOSTAJE (lokalny wydruk). */
const ASSIGN_DOCS = ['product_labels', 'packing_list', 'shipping_label_a6', 'shipping_label_80mm', 'goodspeed_label', 'logo_label', 'reports'];

/* Odmiana po polsku dla banera watchdoga (API oddaje najwyżej 20 zadań): 1 / 2–4 / 5+. */
function stuckJobsLabel(n: number): string {
  if (n === 1) return '1 zadanie czeka';
  if (n < 5) return `${n} zadania czekają`;
  return `${n} zadań czeka`;
}

/* ---------- modal ---------- */
function Modal({ title, icon, onClose, children, footer }: { title: string; icon: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <div className="wiz-scrim" onClick={onClose}>
      <div className="wiz" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="wiz-head">
          <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--accent-soft)', color: 'var(--accent-text)', display: 'grid', placeItems: 'center' }}><Icon name={icon} size={18} /></div>
          <div className="wt">{title}</div>
          <button className="btn btn-icon btn-ghost right" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="wiz-body">{children}</div>
        {footer && <div className="wiz-foot">{footer}</div>}
      </div>
    </div>
  );
}

/* ---------- Drukarki ---------- */
function PrintersTab() {
  const push = useAdminToast();
  const qc = useQueryClient();
  const { data: printers = [] } = useQuery<Printer[]>({
    queryKey: ['settings-printers'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/printers`)).data,
  });
  const { data: assignments = {} } = useQuery<Record<string, string>>({
    queryKey: ['printer-assignments'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/printer-assignments`)).data,
  });
  const { data: printSettings } = useQuery<any>({
    queryKey: ['printing-settings'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/printing`)).data,
  });
  // Watchdog druku: jedno wywołanie zasila baner ostrzeżeń i znaczniki w tabeli. Bez pollingu —
  // wystarczy odczyt przy wejściu w zakładkę i po zapisie drukarki. retry:false, bo starsze API
  // nie ma tego endpointu, a wtedy po prostu nie pokazujemy ostrzeżeń.
  const { data: health } = useQuery<PrintingHealth>({
    queryKey: ['printers-health'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/printers/health`)).data,
    retry: false,
  });
  const { data: agents = [] } = useQuery<PrintAgent[]>({
    queryKey: ['print-agents'],
    queryFn: async () => {
      const d = (await axios.get(`${API_URL}/api/agents`)).data;
      return Array.isArray(d) ? d : d?.agents || [];
    },
  });
  // Do przypisania drukarki proponujemy agentów innych niż odwołani.
  const assignableAgents = agents.filter((a) => a.status !== 'revoked');
  const agentName = (id?: string | null) => agents.find((a) => a.id === id)?.name || '—';
  const [edit, setEdit] = useState<Printer | null>(null);
  const [localAssign, setLocalAssign] = useState<Record<string, string>>({});
  const [localPrint, setLocalPrint] = useState<any>(null);

  useEffect(() => { setLocalAssign(assignments || {}); }, [assignments]);
  useEffect(() => { if (printSettings) setLocalPrint(printSettings); }, [printSettings]);

  // Wiersz tabeli ↔ health: po id, a gdy API poda id w innym kształcie — po nazwie.
  const printerHealth = (p: Printer) =>
    (health?.printers || []).find((hp) => (p.id != null && hp.id === String(p.id)) || hp.name === p.name);
  // Dyskretny znacznik przy wierszu; null = komputer drukujący odpowiada i nie hałasujemy.
  function agentPill(p: Printer) {
    const issue = printerHealth(p)?.issue;
    if (!issue) return null;
    return <span className="pill pill-warn" style={{ height: 20, marginLeft: 6 }}>{issue === 'agent_offline' ? 'agent offline' : 'brak agenta'}</span>;
  }
  // Ostrzeżenia nad tabelą. Pusta lista = wszystko zdrowe i baner się nie renderuje.
  const healthWarnings: string[] = [];
  for (const hp of health?.printers || []) {
    if (hp.issue === 'agent_offline') {
      const seenAt = (health?.agents || []).find((a) => a.id === hp.agentId)?.lastSeenAt;
      healthWarnings.push(`Drukarka ${hp.name}: komputer drukujący ${hp.agentName || '—'} jest offline (ostatni kontakt: ${seenAt ? new Date(seenAt).toLocaleString('pl-PL') : 'brak'})`);
    } else if (hp.issue === 'agent_missing') {
      healthWarnings.push(`Drukarka ${hp.name}: brak przypisanego komputera drukującego`);
    }
  }
  if (health?.stuckJobs?.length) healthWarnings.push(`${stuckJobsLabel(health.stuckJobs.length)} w kolejce dłużej niż 2 minuty`);
  if (health?.duplicateDefaults?.length) healthWarnings.push(`Więcej niż jedna drukarka domyślna: ${health.duplicateDefaults.join(', ')}`);

  async function savePrinter() {
    if (!edit) return;
    try {
      await axios.post(`${API_URL}/api/settings/printers`, edit);
      qc.invalidateQueries({ queryKey: ['settings-printers'] });
      qc.invalidateQueries({ queryKey: ['printers-health'] });
      setEdit(null);
      push('Drukarka zapisana');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zapisu drukarki', 'err'); }
  }
  async function deletePrinter(id?: string) {
    if (!id) return;
    if (!window.confirm('Usunąć drukarkę?')) return;
    try {
      await axios.delete(`${API_URL}/api/settings/printers/${id}`);
      qc.invalidateQueries({ queryKey: ['settings-printers'] });
      qc.invalidateQueries({ queryKey: ['printers-health'] });
      push('Drukarka usunięta');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd usuwania', 'err'); }
  }
  async function setDefault(p: Printer) {
    try {
      await axios.post(`${API_URL}/api/settings/printers`, { ...p, is_default: true });
      qc.invalidateQueries({ queryKey: ['settings-printers'] });
      qc.invalidateQueries({ queryKey: ['printers-health'] });
      push(`${p.name} ustawiona jako domyślna`);
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zapisu', 'err'); }
  }
  async function saveAssignments() {
    try {
      await axios.post(`${API_URL}/api/settings/printer-assignments`, localAssign);
      push('Przypisania drukarek zapisane');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zapisu przypisań', 'err'); }
  }
  async function saveAutoPrint(patch: any) {
    const next = { ...localPrint, ...patch };
    setLocalPrint(next);
    try {
      await axios.post(`${API_URL}/api/settings/printing`, next);
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zapisu ustawień', 'err'); }
  }
  async function testPrint(p: Printer) {
    try {
      await axios.post(`${API_URL}/api/printing/test`, { printerName: p.name });
      push(`Strona testowa → ${p.name}`);
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd druku testowego', 'err'); }
  }

  return (
    <div className="grid" style={{ gap: 20 }}>
      <div className="card">
        <div className="card-head">
          <h3>Drukarki</h3>
          <button className="btn btn-primary btn-sm" onClick={() => setEdit({ name: '', type: 'network', ip: '', port: 9100, protocol: 'RAW', format: 'A4', is_active: true })}>
            <Icon name="plus" size={14} /> Dodaj
          </button>
        </div>
        {healthWarnings.length > 0 && (
          <div className="card-pad" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, color: 'var(--warning-text)', background: 'var(--warning-soft)', borderBottom: '1px solid var(--divider)' }}>
            <Icon name="alert-triangle" size={16} />
            <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
              {healthWarnings.map((w) => <span key={w}>{w}</span>)}
            </div>
          </div>
        )}
        <div className="tablewrap"><table className="tbl">
          <thead><tr><th>Nazwa</th><th>Typ</th><th>Adres</th><th>Agent</th><th>Protokół</th><th>Status</th><th>Domyślna</th><th></th></tr></thead>
          <tbody>{printers.map((p) => (
            <tr key={p.id || p.name}>
              <td style={{ fontWeight: 600 }}>{p.name}</td>
              <td className="muted">{p.type === 'network' ? 'Sieciowa' : p.type === 'system' ? 'Systemowa' : p.type || '—'}</td>
              <td className="mono" style={{ fontSize: 13 }}>{p.ip ? `${p.ip}:${p.port || 9100}` : '—'}</td>
              <td>{p.agent_id
                ? <span style={{ fontSize: 13 }}>{agentName(p.agent_id)}{agentPill(p)}</span>
                : <span className="pill pill-warn">brak agenta</span>}</td>
              <td><span className="tag-attr">{p.protocol || 'RAW'}</span></td>
              <td>{p.is_active !== false
                ? <span className="pill pill-ok"><Icon name="check" size={12} /> OK</span>
                : <span className="pill pill-neutral">Wyłączona</span>}</td>
              <td>{p.is_default
                ? <span className="pill pill-info">Domyślna</span>
                : <button className="btn btn-ghost btn-sm" onClick={() => setDefault(p)}>Ustaw</button>}</td>
              <td className="right">
                <div className="flex" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => testPrint(p)} title="Druk testowy"><Icon name="printer" size={14} /></button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEdit(p)}><Icon name="pencil" size={14} /></button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger-text)' }} onClick={() => deletePrinter(p.id)}><Icon name="trash-2" size={14} /></button>
                </div>
              </td>
            </tr>
          ))}</tbody>
        </table></div>
        {printers.length === 0 && <div className="empty" style={{ padding: '30px 16px' }}><div className="ei"><Icon name="printer" size={26} /></div><h3>Brak drukarek</h3></div>}
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="card">
          <div className="card-head"><h3>Przypisania drukarek</h3><span className="ch-sub">dokument → drukarka</span></div>
          <div className="card-pad" style={{ paddingTop: 8 }}>
            {ASSIGN_DOCS.map((d) => (
              <div className="set-row" key={d}>
                <span className="mono" style={{ fontSize: 13, flex: 1 }}>{d}</span>
                <select className="select" value={localAssign[d] || ''} onChange={(e) => setLocalAssign((a) => ({ ...a, [d]: e.target.value }))}>
                  <option value="">—</option>
                  {printers.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                </select>
              </div>
            ))}
            <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={saveAssignments}><Icon name="save" size={16} /> Zapisz przypisania</button>
          </div>
        </div>
        <div className="card">
          <div className="card-head"><h3>Auto-druk</h3></div>
          <div className="card-pad" style={{ paddingTop: 8 }}>
            <div className="set-row">
              <div className="meta"><div className="t">List pakowania na starcie sesji</div></div>
              <Toggle on={!!localPrint?.auto_print_packing_list} onChange={(v) => saveAutoPrint({ auto_print_packing_list: v })} />
            </div>
            <div className="set-row">
              <div className="meta"><div className="t">Etykiety wysyłkowe po zamknięciu paczki</div></div>
              <Toggle on={!!localPrint?.auto_print_shipping_label} onChange={(v) => saveAutoPrint({ auto_print_shipping_label: v })} />
            </div>
            <div className="set-row">
              <div className="meta"><div className="t">Etykiety produktów</div></div>
              <Toggle on={!!localPrint?.auto_print_product_labels} onChange={(v) => saveAutoPrint({ auto_print_product_labels: v })} />
            </div>
            <div className="set-row">
              <div className="meta">
                <div className="t">Kontrola adresu po wydruku etykiety</div>
                <div className="d">Stacja pokazuje przy potwierdzeniu wydruku surowy adres z zamówienia (linia 1, linia 2, kod pocztowy, miejscowość, uwagi) do porównania z etykietą — tylko przesyłki kurierskie na adres, nie paczkomaty</div>
              </div>
              <Toggle on={!!localPrint?.address_check_after_label} onChange={(v) => saveAutoPrint({ address_check_after_label: v })} />
            </div>
            <div className="set-row">
              <div className="meta"><div className="t">Liczba kopii</div><div className="d">Domyślnie dla etykiet wysyłkowych</div></div>
              <input className="select" style={{ width: 64 }} type="number" min={1} max={10}
                value={localPrint?.copies_shipping_label ?? 1}
                onChange={(e) => saveAutoPrint({ copies_shipping_label: +e.target.value || 1 })} />
            </div>
          </div>
        </div>
      </div>

      <PrintAgentsCard />

      {edit && (
        <Modal title={edit.id ? 'Edytuj drukarkę' : 'Nowa drukarka'} icon="printer" onClose={() => setEdit(null)}
          footer={<>
            <div className="right" />
            <button className="btn btn-ghost" onClick={() => setEdit(null)}>Anuluj</button>
            <button className="btn btn-primary" disabled={!edit.name || !edit.agent_id} onClick={savePrinter}><Icon name="save" size={16} /> Zapisz</button>
          </>}>
          <div className="wfield"><label>Nazwa</label><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="np. ZEBRA-01" autoFocus /></div>
          <div className="wfield-row">
            <div className="wfield"><label>Typ</label>
              <select value={edit.type || 'network'} onChange={(e) => setEdit({ ...edit, type: e.target.value })}>
                <option value="network">Sieciowa</option>
                <option value="system">Systemowa</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
            <div className="wfield"><label>Protokół</label>
              <select value={edit.protocol || 'RAW'} onChange={(e) => setEdit({ ...edit, protocol: e.target.value })}>
                <option>RAW</option><option>EPL</option><option>ZPL</option><option>PDF</option><option>IPP</option>
              </select>
            </div>
          </div>
          <div className="wfield-row">
            <div className="wfield"><label>Adres IP</label><input value={edit.ip || ''} onChange={(e) => setEdit({ ...edit, ip: e.target.value })} placeholder="10.0.5.21" /></div>
            <div className="wfield"><label>Port</label><input type="number" value={edit.port || 9100} onChange={(e) => setEdit({ ...edit, port: +e.target.value })} /></div>
          </div>
          <div className="wfield"><label>Format</label>
            <select value={edit.format || 'A4'} onChange={(e) => setEdit({ ...edit, format: e.target.value })}>
              <option>A4</option><option>A6</option><option>80x50mm</option><option>100x150mm</option>
            </select>
          </div>
          <div className="wfield"><label>Agent (komputer drukujący)</label>
            <select value={edit.agent_id || ''} onChange={(e) => setEdit({ ...edit, agent_id: e.target.value || null })}>
              <option value="">— wybierz agenta —</option>
              {assignableAgents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            {assignableAgents.length === 0 && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Brak agentów — najpierw sparuj agenta (Insolitum Cloud Print) poniżej.</div>
            )}
          </div>
          <div className="set-row" style={{ border: 0, padding: '6px 0' }}>
            <div className="meta"><div className="t">Odwróć kolory (negatyw)</div><div className="d">Włącz dla XPrinter drukującego w negatywie (białe na czarnym)</div></div>
            <Toggle on={!!edit.invert_colors} onChange={(v) => setEdit({ ...edit, invert_colors: v })} />
          </div>
          <div className="set-row" style={{ border: 0, padding: '6px 0' }}>
            <div className="meta"><div className="t">Aktywna</div></div>
            <Toggle on={edit.is_active !== false} onChange={(v) => setEdit({ ...edit, is_active: v })} />
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Agenci druku (on-prem) ---------- */
function PrintAgentsCard() {
  const push = useAdminToast();
  const qc = useQueryClient();
  const { data: agents = [] } = useQuery<any[]>({
    queryKey: ['print-agents'],
    queryFn: async () => {
      const d = (await axios.get(`${API_URL}/api/agents`)).data;
      return Array.isArray(d) ? d : d?.agents || [];
    },
  });
  // Wydanie aplikacji desktopowej (Electron „Insolitum Cloud Print") z bucketu agent-desktop:
  // 200 z {version,downloads} albo 204, gdy jeszcze nie zbudowano (tag agent-desktop-v*).
  const { data: desktop } = useQuery<{ version: string; downloads: Partial<Record<'win' | 'mac' | 'linux', { url: string }>> } | null>({
    queryKey: ['agent-desktop-release'],
    queryFn: async () => {
      const r = await axios.get(`${API_URL}/api/agents/desktop-release`, { validateStatus: (s) => s === 200 || s === 204 });
      return r.status === 200 ? r.data : null;
    },
  });
  const dl = desktop?.downloads || {};
  const [name, setName] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [bootstrapCfg, setBootstrapCfg] = useState<any | null>(null);
  const [pair, setPair] = useState<{ code: string; expiresAt: number } | null>(null);
  const [remain, setRemain] = useState(0);
  useEffect(() => {
    if (!pair) return;
    const tick = () => {
      const r = Math.max(0, Math.round((pair.expiresAt - Date.now()) / 1000));
      setRemain(r);
      if (r <= 0) setPair(null);
    };
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [pair]);

  // Parowanie kodem 6-cyfrowym dla aplikacji desktopowej (jak stacje/urządzenia).
  async function genPairingCode() {
    try {
      const body = name.trim() ? { name: name.trim() } : {};
      const r = await axios.post(`${API_URL}/api/agents/pairing-code`, body);
      setPair({ code: r.data.code, expiresAt: Date.now() + (r.data.expires_in_sec || 600) * 1000 });
      qc.invalidateQueries({ queryKey: ['print-agents'] });
      push('Kod parowania wygenerowany');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd generowania kodu', 'err'); }
  }
  // Ścieżka zaawansowana: sam config.json dla agenta konsolowego (headless).
  async function bootstrap() {
    const n = name.trim();
    if (!n) { push('Podaj nazwę agenta (np. magazyn-pc1)', 'err'); return; }
    try {
      const r = await axios.post(`${API_URL}/api/agents/bootstrap`, { name: n });
      setBootstrapCfg(r.data?.config || { bootstrap_token: r.data?.bootstrap_token });
      qc.invalidateQueries({ queryKey: ['print-agents'] });
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd generowania configu', 'err'); }
  }
  async function revoke(id: string) {
    try {
      await axios.post(`${API_URL}/api/agents/${id}/revoke`);
      qc.invalidateQueries({ queryKey: ['print-agents'] });
      push('Agent odwołany');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd odwołania agenta', 'err'); }
  }
  // Edycja (zmiana nazwy) agenta — inline w wierszu tabeli.
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  function startEdit(a: any) { setEditId(a.id); setEditName(a.name || ''); }
  async function rename(id: string) {
    const n = editName.trim();
    if (!n) { push('Podaj nazwę agenta', 'err'); return; }
    try {
      await axios.patch(`${API_URL}/api/agents/${id}`, { name: n });
      qc.invalidateQueries({ queryKey: ['print-agents'] });
      setEditId(null);
      push('Nazwa agenta zmieniona');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zmiany nazwy', 'err'); }
  }
  async function remove(id: string, label: string) {
    if (!window.confirm(`Usunąć agenta „${label}"? Tej operacji nie można cofnąć.`)) return;
    try {
      await axios.delete(`${API_URL}/api/agents/${id}`);
      qc.invalidateQueries({ queryKey: ['print-agents'] });
      if (editId === id) setEditId(null);
      push('Agent usunięty');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd usuwania agenta', 'err'); }
  }

  return (
    <div className="card">
      <div className="card-head">
        <div><h3>Agenci druku (on-prem)</h3><div className="ch-sub">Lokalny agent łączy drukarki sieciowe z chmurą PackFlow</div></div>
      </div>
      <div className="card-pad" style={{ borderBottom: '1px solid var(--divider)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="select" placeholder="Nazwa agenta (opcjonalnie), np. magazyn-pc1" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: '1 1 220px', minWidth: 200 }} />
        <button className="btn btn-primary btn-sm" onClick={genPairingCode}><Icon name="key-round" size={14} /> Generuj kod parowania</button>
        {(['win', 'mac', 'linux'] as const).map((p) => {
          const label = p === 'win' ? 'Windows' : p === 'mac' ? 'macOS' : 'Linux';
          return dl[p]?.url
            ? <a key={p} className="btn btn-ghost btn-sm" href={dl[p]!.url} target="_blank" rel="noreferrer"><Icon name="download" size={14} /> {label}</a>
            : <button key={p} className="btn btn-ghost btn-sm" disabled title="Aplikacja zostanie opublikowana po pierwszym buildzie (tag agent-desktop-v*)"><Icon name="download" size={14} /> {label}</button>;
        })}
        {desktop
          ? <span className="muted" style={{ fontSize: 12, flexBasis: '100%' }}>Aplikacja <strong>Insolitum Cloud Print v{desktop.version}</strong> — zainstaluj, uruchom i wpisz kod parowania.</span>
          : <div className="muted" style={{ fontSize: 12, flexBasis: '100%' }}>Aplikacja desktopowa będzie do pobrania po pierwszym buildzie (tag <code>agent-desktop-v*</code>). „Generuj kod parowania" działa już teraz.</div>}
        <div style={{ flexBasis: '100%' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdvanced((v) => !v)} style={{ padding: '2px 4px', fontSize: 12 }}>
            <Icon name={advanced ? 'chevron-down' : 'chevron-right'} size={14} /> Zaawansowane
          </button>
          {advanced && (
            <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-ghost btn-sm" onClick={bootstrap} title="config.json dla agenta konsolowego (headless)"><Icon name="key-round" size={14} /> Tylko config</button>
              <span className="muted" style={{ fontSize: 12 }}>Dla instalacji headless/serwerowych bez pulpitu — agent konsolowy z plikiem <code>config.json</code> zamiast aplikacji desktop.</span>
            </div>
          )}
        </div>
      </div>
      {pair && (
        <div className="card-pad" style={{ background: 'var(--accent-soft)', borderBottom: '1px solid var(--divider)' }}>
          <div style={{ fontSize: 12, color: 'var(--accent-text)', fontWeight: 700, marginBottom: 6 }}>Wpisz ten kod w aplikacji Insolitum Cloud Print (ważny {Math.floor(remain / 60)}:{String(remain % 60).padStart(2, '0')}):</div>
          <div className="mono" style={{ fontSize: 30, letterSpacing: '0.3em', fontWeight: 700 }}>{pair.code}</div>
        </div>
      )}
      {bootstrapCfg && (
        <div className="card-pad" style={{ background: 'var(--accent-soft)', borderBottom: '1px solid var(--divider)' }}>
          <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-text)' }}>Jednorazowa konfiguracja (24 h) — zapisz jako <span className="mono">config.json</span> obok pliku agenta:</div>
            <button className="btn btn-primary btn-sm" onClick={() => {
              const blob = new Blob([JSON.stringify(bootstrapCfg, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = 'config.json'; a.click();
              URL.revokeObjectURL(url);
            }}><Icon name="download" size={14} /> Pobierz config.json</button>
          </div>
          <code className="mono" style={{ fontSize: 12, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>{JSON.stringify(bootstrapCfg, null, 2)}</code>
        </div>
      )}
      <div className="tablewrap"><table className="tbl">
        <thead><tr><th>Agent</th><th>Ostatnio widziany</th><th>Status</th><th></th></tr></thead>
        <tbody>{agents.map((a: any) => (
          <tr key={a.id}>
            <td style={{ fontWeight: 600 }}>
              {editId === a.id ? (
                <input
                  className="select"
                  value={editName}
                  autoFocus
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') rename(a.id); if (e.key === 'Escape') setEditId(null); }}
                  style={{ maxWidth: 260 }}
                />
              ) : (a.name || a.id)}
            </td>
            <td className="mono muted" style={{ fontSize: 13 }}>{a.last_seen_at ? new Date(a.last_seen_at).toLocaleString('pl-PL') : '—'}</td>
            <td>{a.status === 'revoked'
              ? <span className="pill pill-neutral">Odwołany</span>
              : a.status === 'pending'
                ? <span className="pill pill-warn">Oczekuje na parowanie</span>
                : isAgentOnline(a)
                  ? <span className="pill pill-ok"><Icon name="check" size={12} /> Online</span>
                  : <span className="pill pill-warn"><Icon name="cloud-off" size={12} /> Offline</span>}</td>
            <td className="right">
              <div className="flex" style={{ gap: 6, justifyContent: 'flex-end' }}>
                {editId === a.id ? (
                  <>
                    <button className="btn btn-icon btn-primary btn-sm" title="Zapisz" aria-label="Zapisz" onClick={() => rename(a.id)}><Icon name="check" size={15} /></button>
                    <button className="btn btn-icon btn-ghost btn-sm" title="Anuluj" aria-label="Anuluj" onClick={() => setEditId(null)}><Icon name="x" size={15} /></button>
                  </>
                ) : (
                  <>
                    <button className="btn btn-icon btn-ghost btn-sm" title="Edytuj" aria-label="Edytuj" onClick={() => startEdit(a)}><Icon name="pencil" size={15} /></button>
                    {a.status !== 'revoked' && (
                      <button className="btn btn-icon btn-ghost btn-sm" title="Odwołaj" aria-label="Odwołaj" style={{ color: 'var(--warning-text)' }} onClick={() => revoke(a.id)}><Icon name="ban" size={15} /></button>
                    )}
                    <button className="btn btn-icon btn-ghost btn-sm" title="Usuń" aria-label="Usuń" style={{ color: 'var(--danger-text)' }} onClick={() => remove(a.id, a.name || a.id)}><Icon name="trash-2" size={15} /></button>
                  </>
                )}
              </div>
            </td>
          </tr>
        ))}</tbody>
      </table></div>
      {agents.length === 0 && <div className="card-pad muted" style={{ fontSize: 13 }}>Brak zarejestrowanych agentów.</div>}
    </div>
  );
}

/* ---------- Typy paczek ---------- */
function PackagesTab() {
  const push = useAdminToast();
  const qc = useQueryClient();
  const { data: types = [] } = useQuery<PackageType[]>({
    queryKey: ['package-types'],
    queryFn: async () => {
      const data = (await axios.get(`${API_URL}/api/settings/package-types`)).data;
      return (Array.isArray(data) ? data : []).map((p: any) => ({ ...p, category_ids: parseCatIds(p.category_ids) }));
    },
  });
  // Kategorie kurierskie z UCS (proxy /api/ucs/categories). Brak dostępu/UCS → pusta lista.
  const { data: categories = [] } = useQuery<UcsCategory[]>({
    queryKey: ['ucs-categories'],
    queryFn: async () => {
      const data = (await axios.get(`${API_URL}/api/ucs/categories`)).data;
      const raw = data?.categories ?? data;
      return Array.isArray(raw) ? raw : [];
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const [edit, setEdit] = useState<PackageType | null>(null);
  // Przypisane id, których UCS już nie zna (kategoria usunięta/odtworzona w UCS). Edytor nie miał
  // dla nich pola, więc nie dało się ich odznaczyć i zostawały w kartonie na zawsze. Liczymy je
  // tylko gdy lista kategorii się wczytała — inaczej (UCS niedostępny) każde id byłoby „usunięte”.
  const orphanIds = (ids?: string[]) =>
    categories.length > 0 ? (ids || []).filter((id) => !categories.some((c) => c.id === id)) : [];

  function toggleCategory(catId: string) {
    if (!edit) return;
    const cur = edit.category_ids || [];
    setEdit({ ...edit, category_ids: cur.includes(catId) ? cur.filter((c) => c !== catId) : [...cur, catId] });
  }

  async function save() {
    if (!edit) return;
    try {
      await axios.post(`${API_URL}/api/settings/package-types`, edit);
      qc.invalidateQueries({ queryKey: ['package-types'] });
      setEdit(null);
      push('Typ paczki zapisany');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zapisu', 'err'); }
  }
  async function remove(id?: string) {
    if (!id || !window.confirm('Usunąć typ paczki?')) return;
    try {
      await axios.delete(`${API_URL}/api/settings/package-types/${id}`);
      qc.invalidateQueries({ queryKey: ['package-types'] });
      push('Typ paczki usunięty');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd usuwania', 'err'); }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Typy paczek</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setEdit({ name: '', length: 30, width: 25, height: 15, weight: 0.5, category_ids: [], is_active: true })}><Icon name="plus" size={14} /> Dodaj</button>
      </div>
      <div className="tablewrap"><table className="tbl">
        <thead><tr><th>Nazwa</th><th>Wymiary (cm)</th><th>Waga</th><th>Kategorie</th><th>Status</th><th></th></tr></thead>
        <tbody>{types.map((p) => {
          const orphans = orphanIds(p.category_ids);
          const catNames = (p.category_ids || []).map((id) => ({ id, name: categories.find((c) => c.id === id)?.name || id, orphan: orphans.includes(id) }));
          return (
          <tr key={p.id || p.name}>
            <td style={{ fontWeight: 600 }}>{p.name}</td>
            <td><span className="mono">{p.length}×{p.width}×{p.height}</span></td>
            <td className="num">{p.weight} kg</td>
            <td>{catNames.length === 0
              ? <span className="muted" style={{ fontSize: 12 }}>wszystkie</span>
              : <span className="flex" style={{ flexWrap: 'wrap', gap: 4 }}>{catNames.map((n) => n.orphan
                  ? <span key={n.id} className="pill pill-warn" style={{ height: 20 }} title={n.id}>usunięta kategoria</span>
                  : <span key={n.id} className="pill pill-neutral" style={{ height: 20 }}>{n.name}</span>)}</span>}</td>
            <td>{p.is_active !== false ? <span className="pill pill-ok">Aktywny</span> : <span className="pill pill-neutral">Wyłączony</span>}</td>
            <td className="right"><div className="flex" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setEdit(p)}><Icon name="pencil" size={14} /></button>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger-text)' }} onClick={() => remove(p.id)}><Icon name="trash-2" size={14} /></button>
            </div></td>
          </tr>
          );
        })}</tbody>
      </table></div>
      {types.length === 0 && <div className="empty" style={{ padding: '30px 16px' }}><div className="ei"><Icon name="box" size={26} /></div><h3>Brak typów paczek</h3></div>}

      {edit && (
        <Modal title={edit.id ? 'Edytuj typ paczki' : 'Nowy typ paczki'} icon="box" onClose={() => setEdit(null)}
          footer={<>
            <div className="right" />
            <button className="btn btn-ghost" onClick={() => setEdit(null)}>Anuluj</button>
            <button className="btn btn-primary" disabled={!edit.name} onClick={save}><Icon name="save" size={16} /> Zapisz</button>
          </>}>
          <div className="wfield"><label>Nazwa</label><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="np. Karton M" autoFocus /></div>
          <div className="wfield-row">
            <div className="wfield"><label>Długość (cm)</label><input type="number" value={edit.length || 0} onChange={(e) => setEdit({ ...edit, length: +e.target.value })} /></div>
            <div className="wfield"><label>Szerokość (cm)</label><input type="number" value={edit.width || 0} onChange={(e) => setEdit({ ...edit, width: +e.target.value })} /></div>
          </div>
          <div className="wfield-row">
            <div className="wfield"><label>Wysokość (cm)</label><input type="number" value={edit.height || 0} onChange={(e) => setEdit({ ...edit, height: +e.target.value })} /></div>
            <div className="wfield"><label>Waga opakowania (kg)</label><input type="number" step="0.1" value={edit.weight || 0} onChange={(e) => setEdit({ ...edit, weight: +e.target.value })} /></div>
          </div>
          {categories.length > 0 && (
            <div className="wfield">
              <label>Kategorie kurierskie</label>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>W których metodach dostępny jest ten karton. Nic nie zaznaczone = wszystkie.</div>
              <div className="grid" style={{ gap: 6 }}>
                {[...categories, ...orphanIds(edit.category_ids).map((id) => ({ id, name: 'Usunięta kategoria', orphan: true }))].map((c) => {
                  const on = (edit.category_ids || []).includes(c.id);
                  const orphan = 'orphan' in c;
                  return (
                    <button key={c.id} type="button" onClick={() => toggleCategory(c.id)} className="card"
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'var(--fg-1)', border: `1px solid ${on ? 'var(--accent)' : 'var(--divider)'}`, background: on ? 'var(--accent-soft)' : 'transparent' }}>
                      <div style={{ width: 18, height: 18, borderRadius: 5, flex: 'none', display: 'grid', placeItems: 'center', border: `2px solid ${on ? 'var(--accent)' : 'var(--fg-3)'}`, background: on ? 'var(--accent)' : 'transparent', color: '#fff' }}>
                        {on && <Icon name="check" size={12} />}
                      </div>
                      <span style={{ fontWeight: 600 }}>{c.name}</span>
                      {orphan && <span className="muted mono" style={{ fontSize: 11 }}>{c.id} — odznacz, by usunąć</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="set-row" style={{ border: 0, padding: '6px 0' }}>
            <div className="meta"><div className="t">Aktywny</div></div>
            <Toggle on={edit.is_active !== false} onChange={(v) => setEdit({ ...edit, is_active: v })} />
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Operatorzy ---------- */
function OperatorsTab() {
  const push = useAdminToast();
  const qc = useQueryClient();
  const { data: users = [] } = useQuery<OrgUser[]>({
    queryKey: ['settings-users'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/users`)).data,
  });
  const [edit, setEdit] = useState<OrgUser | null>(null);

  async function save() {
    if (!edit) return;
    try {
      if (edit.id) await axios.put(`${API_URL}/api/settings/users/${edit.id}`, edit);
      else await axios.post(`${API_URL}/api/settings/users`, edit);
      qc.invalidateQueries({ queryKey: ['settings-users'] });
      setEdit(null);
      push('Operator zapisany');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zapisu operatora', 'err'); }
  }
  async function remove(id?: string) {
    if (!id || !window.confirm('Usunąć operatora?')) return;
    try {
      await axios.delete(`${API_URL}/api/settings/users/${id}`);
      qc.invalidateQueries({ queryKey: ['settings-users'] });
      push('Operator usunięty');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd usuwania', 'err'); }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Operatorzy</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setEdit({ first_name: '', last_name: '', pin: '', role: 'operator', is_active: true })}><Icon name="plus" size={14} /> Dodaj</button>
      </div>
      <div className="tablewrap"><table className="tbl">
        <thead><tr><th>Imię</th><th>Rola</th><th>PIN</th><th>Ostatnie logowanie</th><th>Status</th><th></th></tr></thead>
        <tbody>{users.map((u) => (
          <tr key={u.id}>
            <td style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}</td>
            <td><span className="pill pill-neutral">{u.role}</span></td>
            <td><span className="mono">••••</span></td>
            <td className="mono muted" style={{ fontSize: 13 }}>{u.last_login_at ? new Date(u.last_login_at).toLocaleString('pl-PL') : '—'}</td>
            <td>{u.is_active !== false ? <span className="pill pill-ok">Aktywny</span> : <span className="pill pill-neutral">Wyłączony</span>}</td>
            <td className="right"><div className="flex" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setEdit({ ...u, pin: '' })}><Icon name="pencil" size={14} /></button>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger-text)' }} onClick={() => remove(u.id)}><Icon name="trash-2" size={14} /></button>
            </div></td>
          </tr>
        ))}</tbody>
      </table></div>
      {users.length === 0 && <div className="empty" style={{ padding: '30px 16px' }}><div className="ei"><Icon name="users" size={26} /></div><h3>Brak operatorów</h3></div>}

      {edit && (
        <Modal title={edit.id ? 'Edytuj operatora' : 'Nowy operator'} icon="users" onClose={() => setEdit(null)}
          footer={<>
            <div className="right" />
            <button className="btn btn-ghost" onClick={() => setEdit(null)}>Anuluj</button>
            <button className="btn btn-primary" disabled={!edit.first_name || !edit.last_name || (!edit.id && !edit.pin)} onClick={save}><Icon name="save" size={16} /> Zapisz</button>
          </>}>
          <div className="wfield-row">
            <div className="wfield"><label>Imię</label><input value={edit.first_name} onChange={(e) => setEdit({ ...edit, first_name: e.target.value })} autoFocus /></div>
            <div className="wfield"><label>Nazwisko</label><input value={edit.last_name} onChange={(e) => setEdit({ ...edit, last_name: e.target.value })} /></div>
          </div>
          <div className="wfield-row">
            <div className="wfield"><label>PIN (4 cyfry){edit.id ? ' — puste = bez zmian' : ''}</label>
              <input value={edit.pin || ''} maxLength={4} onChange={(e) => setEdit({ ...edit, pin: e.target.value.replace(/\D/g, '') })} placeholder="••••" /></div>
            <div className="wfield"><label>Rola</label>
              <select value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}>
                <option value="operator">operator</option>
                <option value="lead">lead</option>
                <option value="trainee">trainee</option>
              </select>
            </div>
          </div>
          <div className="set-row" style={{ border: 0, padding: '6px 0' }}>
            <div className="meta"><div className="t">Aktywny</div></div>
            <Toggle on={edit.is_active !== false} onChange={(v) => setEdit({ ...edit, is_active: v })} />
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Stacje ---------- */
type StationPairing = { id: string; station_id: string; paired_status?: string; last_seen_at?: string; has_pending_code?: boolean };

function StationsTabSettings() {
  const push = useAdminToast();
  const qc = useQueryClient();
  const { data: stations = [] } = useQuery<Workstation[]>({
    queryKey: ['workstations'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/workstations`)).data,
  });
  const { data: pairing = [] } = useQuery<StationPairing[]>({
    queryKey: ['stations-pairing'],
    queryFn: async () => {
      const d = (await axios.get(`${API_URL}/api/stations`)).data;
      return Array.isArray(d) ? d : d?.stations || [];
    },
    refetchInterval: 15000,
  });
  const [edit, setEdit] = useState<Workstation | null>(null);
  const [code, setCode] = useState<{ station: string; code: string } | null>(null);
  const pairOf = (sid: string) => pairing.find((p) => p.station_id === sid);
  const { data: desktop } = useQuery<{ version: string; downloads: Partial<Record<'win' | 'mac' | 'linux', { url: string }>> } | null>({
    queryKey: ['desktop-release'],
    queryFn: async () => {
      const r = await axios.get(`${API_URL}/api/stations/desktop-release`, {
        validateStatus: (s) => s === 200 || s === 204,
      });
      return r.status === 200 ? r.data : null;
    },
  });

  async function save() {
    if (!edit) return;
    try {
      if (edit.id) await axios.put(`${API_URL}/api/settings/workstations/${edit.id}`, edit);
      else await axios.post(`${API_URL}/api/settings/workstations`, edit);
      qc.invalidateQueries({ queryKey: ['workstations'] });
      qc.invalidateQueries({ queryKey: ['stations-pairing'] });
      setEdit(null);
      push('Stacja zapisana');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zapisu stacji', 'err'); }
  }
  async function remove(id?: string) {
    if (!id || !window.confirm('Usunąć stację?')) return;
    try {
      await axios.delete(`${API_URL}/api/settings/workstations/${id}`);
      qc.invalidateQueries({ queryKey: ['workstations'] });
      qc.invalidateQueries({ queryKey: ['stations-pairing'] });
      push('Stacja usunięta');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd usuwania', 'err'); }
  }
  async function genCode(s: Workstation) {
    try {
      const r = await axios.post(`${API_URL}/api/stations/pairing-code`, { station_id: s.station_id });
      setCode({ station: s.name || s.station_id, code: r.data?.code });
      qc.invalidateQueries({ queryKey: ['stations-pairing'] });
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd generowania kodu', 'err'); }
  }
  async function unpair(s: Workstation) {
    if (!s.id || !window.confirm('Rozparować to stanowisko?')) return;
    try {
      await axios.post(`${API_URL}/api/stations/${s.id}/unpair`);
      qc.invalidateQueries({ queryKey: ['stations-pairing'] });
      push('Stanowisko rozparowane');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd rozparowania', 'err'); }
  }

  function PairBadge({ sid }: { sid: string }) {
    const p = pairOf(sid);
    if (p?.paired_status === 'active') return <span className="pill pill-ok"><Icon name="check" size={12} /> Sparowane</span>;
    if (p?.paired_status === 'pending' || p?.has_pending_code) return <span className="pill pill-warn">Oczekuje</span>;
    return <span className="pill pill-neutral">Niesparowane</span>;
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Stacje robocze</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setEdit({ station_id: '', name: '', location: '', is_active: true })}><Icon name="plus" size={14} /> Dodaj</button>
      </div>
      <div className="card-pad" style={{ paddingTop: 10, paddingBottom: 4 }}>
        <div className="flex" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="ch-sub" style={{ flex: '1 1 320px' }}>Aplikacja PackFlow Station (kiosk) — instalacja pełnoekranowa z autostartem, aktywacja kodem.</span>
          {(['win', 'mac', 'linux'] as const).map((p) => {
            const label = p === 'win' ? 'Windows' : p === 'mac' ? 'macOS' : 'Linux';
            return desktop?.downloads?.[p]?.url ? (
              <a key={p} className="btn btn-ghost btn-sm" href={`${API_URL}/api/stations/download?platform=${p}`} target="_blank" rel="noreferrer"><Icon name="download" size={14} /> {label}</a>
            ) : (
              <button key={p} className="btn btn-ghost btn-sm" disabled title="Dostępne po pierwszym buildzie (tag station-desktop-v*)"><Icon name="download" size={14} /> {label}</button>
            );
          })}
        </div>
        {code && (
          <div style={{ marginTop: 12, padding: '12px 14px', background: 'var(--accent-soft)', borderRadius: 10, border: '1px solid var(--divider)' }}>
            <div style={{ fontSize: 12, color: 'var(--accent-text)', fontWeight: 700, marginBottom: 6 }}>Kod parowania dla „{code.station}" (ważny 10 min):</div>
            <div className="mono" style={{ fontSize: 28, letterSpacing: '0.3em', fontWeight: 700 }}>{code.code}</div>
          </div>
        )}
      </div>
      <div className="tablewrap"><table className="tbl">
        <thead><tr><th>ID</th><th>Nazwa</th><th>Lokalizacja</th><th>Status</th><th>Urządzenie</th><th></th></tr></thead>
        <tbody>{stations.map((s) => {
          const paired = pairOf(s.station_id)?.paired_status === 'active';
          return (
          <tr key={s.id || s.station_id}>
            <td><span className="mono" style={{ fontWeight: 700 }}>{s.station_id}</span></td>
            <td>{s.name}</td>
            <td className="muted">{s.location || '—'}</td>
            <td>{s.is_active !== false ? <span className="pill pill-ok">Aktywna</span> : <span className="pill pill-neutral">Wyłączona</span>}</td>
            <td><PairBadge sid={s.station_id} /></td>
            <td className="right"><div className="flex" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" title="Generuj kod parowania" onClick={() => genCode(s)}><Icon name="key-round" size={14} /></button>
              {paired && <button className="btn btn-ghost btn-sm" title="Rozparuj" onClick={() => unpair(s)}><Icon name="unlink" size={14} /></button>}
              <button className="btn btn-ghost btn-sm" onClick={() => setEdit(s)}><Icon name="pencil" size={14} /></button>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger-text)' }} onClick={() => remove(s.id)}><Icon name="trash-2" size={14} /></button>
            </div></td>
          </tr>
        ); })}</tbody>
      </table></div>
      {stations.length === 0 && <div className="empty" style={{ padding: '30px 16px' }}><div className="ei"><Icon name="cpu" size={26} /></div><h3>Brak stacji</h3></div>}

      {edit && (
        <Modal title={edit.id ? 'Edytuj stację' : 'Nowa stacja'} icon="cpu" onClose={() => setEdit(null)}
          footer={<>
            <div className="right" />
            <button className="btn btn-ghost" onClick={() => setEdit(null)}>Anuluj</button>
            <button className="btn btn-primary" disabled={!edit.station_id || !edit.name} onClick={save}><Icon name="save" size={16} /> Zapisz</button>
          </>}>
          <div className="wfield-row">
            <div className="wfield"><label>ID stacji</label><input value={edit.station_id} onChange={(e) => setEdit({ ...edit, station_id: e.target.value.toUpperCase() })} placeholder="PACK-01" autoFocus /></div>
            <div className="wfield"><label>Nazwa</label><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Stacja pakowania 01" /></div>
          </div>
          <div className="wfield-row">
            <div className="wfield"><label>Lokalizacja</label><input value={edit.location || ''} onChange={(e) => setEdit({ ...edit, location: e.target.value })} placeholder="Hala A" /></div>
            <div className="wfield"><label>Opis</label><input value={edit.description || ''} onChange={(e) => setEdit({ ...edit, description: e.target.value })} placeholder="opcjonalny" /></div>
          </div>
          <div className="wfield-row">
            <div className="wfield">
              <label>Skala interfejsu (%)</label>
              <input
                type="number"
                min={50}
                max={300}
                step={5}
                value={edit.ui_scale ?? 100}
                onChange={(e) => setEdit({ ...edit, ui_scale: clampScale(+e.target.value) })}
                placeholder="100"
              />
              <small style={{ color: 'var(--fg-3)' }}>Powiększa cały kiosk (np. 150% na dużym ekranie).</small>
            </div>
            <div className="wfield">
              <label>Rozmiar tekstu (%)</label>
              <input
                type="number"
                min={50}
                max={300}
                step={5}
                value={edit.text_scale ?? 100}
                onChange={(e) => setEdit({ ...edit, text_scale: clampScale(+e.target.value) })}
                placeholder="100"
              />
              <small style={{ color: 'var(--fg-3)' }}>Dodatkowo powiększa sam tekst. Działa po kolejnym logowaniu stacji.</small>
            </div>
          </div>
          <div className="set-row" style={{ border: 0, padding: '6px 0' }}>
            <div className="meta"><div className="t">Aktywna</div></div>
            <Toggle on={edit.is_active !== false} onChange={(v) => setEdit({ ...edit, is_active: v })} />
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Urządzenia mobilne ---------- */
function DevicesTab() {
  const push = useAdminToast();
  const qc = useQueryClient();
  const { data: devices = [] } = useQuery<Device[]>({
    queryKey: ['mobile-devices'],
    queryFn: async () => {
      const d = (await axios.get(`${API_URL}/api/devices`)).data;
      return Array.isArray(d) ? d : d?.devices || [];
    },
    refetchInterval: 15000,
  });
  const { data: stations = [] } = useQuery<Workstation[]>({
    queryKey: ['workstations'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/workstations`)).data,
  });
  const [name, setName] = useState('');
  const [stationId, setStationId] = useState('');
  const [code, setCode] = useState<{ label: string; code: string } | null>(null);
  const [edit, setEdit] = useState<Device | null>(null);
  const { data: apk } = useQuery<{ version: string; url?: string } | null>({
    queryKey: ['android-release'],
    queryFn: async () => {
      const r = await axios.get(`${API_URL}/api/devices/android-release`, {
        validateStatus: (s) => s === 200 || s === 204,
      });
      return r.status === 200 ? r.data : null;
    },
  });

  async function generate() {
    try {
      const r = await axios.post(`${API_URL}/api/devices/pairing-code`, {
        name: name.trim() || undefined,
        station_id: stationId || undefined,
      });
      setCode(r.data?.code ? { label: name.trim() || 'nowe urządzenie', code: r.data.code } : null);
      setName('');
      qc.invalidateQueries({ queryKey: ['mobile-devices'] });
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd generowania kodu', 'err'); }
  }
  async function genCodeFor(d: Device) {
    try {
      const r = await axios.post(`${API_URL}/api/devices/pairing-code`, {
        name: d.name || undefined,
        station_id: d.station_id || undefined,
      });
      setCode(r.data?.code ? { label: d.name || 'urządzenie', code: r.data.code } : null);
      qc.invalidateQueries({ queryKey: ['mobile-devices'] });
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd generowania kodu', 'err'); }
  }
  async function saveEdit() {
    if (!edit) return;
    try {
      await axios.put(`${API_URL}/api/devices/${edit.id}`, { name: edit.name, station_id: edit.station_id || null });
      qc.invalidateQueries({ queryKey: ['mobile-devices'] });
      setEdit(null);
      push('Urządzenie zapisane');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zapisu urządzenia', 'err'); }
  }
  async function revoke(id: string) {
    if (!window.confirm('Odłączyć to urządzenie?')) return;
    try {
      await axios.post(`${API_URL}/api/devices/${id}/revoke`);
      qc.invalidateQueries({ queryKey: ['mobile-devices'] });
      push('Urządzenie odłączone');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd odłączania', 'err'); }
  }
  const stationName = (sid?: string | null) => stations.find((s) => s.station_id === sid)?.name || sid || '—';

  return (
    <div className="card">
      <div className="card-head">
        <div><h3>Urządzenia mobilne</h3><div className="ch-sub">Aplikacja Android (skaner/pakowanie) parowana 6-cyfrowym kodem z tą organizacją</div></div>
      </div>
      <div className="card-pad" style={{ paddingTop: 8 }}>
        <div className="flex" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="select" style={{ flex: '1 1 240px' }} placeholder="Nazwa urządzenia (opcjonalnie), np. pakownia-telefon-1" value={name} onChange={(e) => setName(e.target.value)} />
          <select className="select" style={{ flex: '0 1 220px' }} value={stationId} onChange={(e) => setStationId(e.target.value)}>
            <option value="">— wybierz stanowisko —</option>
            {stations.map((s) => <option key={s.station_id} value={s.station_id}>{s.name || s.station_id}</option>)}
          </select>
          <button className="btn btn-primary" onClick={generate}><Icon name="key-round" size={15} /> Generuj kod parowania</button>
          {apk?.url ? (
            <a className="btn btn-ghost" href={apk.url} target="_blank" rel="noreferrer"><Icon name="download" size={15} /> Pobierz APK ({apk.version})</a>
          ) : (
            <button className="btn btn-ghost" disabled title="APK dostępny po pierwszym buildzie (tag android-v*)"><Icon name="download" size={15} /> Pobierz aplikację (APK)</button>
          )}
        </div>
        <div className="ch-sub" style={{ marginTop: 8 }}>{apk?.url
          ? <>Najnowszy build APK: <span className="mono">{apk.version}</span>. „Generuj kod parowania" tworzy 6-cyfrowy kod ważny 10 min.</>
          : <>APK będzie do pobrania po pierwszym buildzie (tag <span className="mono">android-v*</span>). „Generuj kod parowania" działa już teraz.</>}</div>
        {code && (
          <div style={{ marginTop: 14, padding: '14px 16px', background: 'var(--accent-soft)', borderRadius: 10, border: '1px solid var(--divider)' }}>
            <div style={{ fontSize: 12, color: 'var(--accent-text)', fontWeight: 700, marginBottom: 6 }}>Kod parowania dla „{code.label}" (ważny 10 min):</div>
            <div className="mono" style={{ fontSize: 30, letterSpacing: '0.3em', fontWeight: 700 }}>{code.code}</div>
          </div>
        )}
      </div>
      <div className="tablewrap"><table className="tbl">
        <thead><tr><th>Urządzenie</th><th>Stanowisko</th><th>Wersja</th><th>Ostatnio widziane</th><th>Status</th><th></th></tr></thead>
        <tbody>{devices.map((d) => (
          <tr key={d.id}>
            <td style={{ fontWeight: 600 }}>{d.name}{d.model ? <div className="muted" style={{ fontSize: 12 }}>{d.model}</div> : null}</td>
            <td className="muted">{stationName(d.station_id)}</td>
            <td className="mono muted" style={{ fontSize: 13 }}>{d.app_version || '—'}</td>
            <td className="mono muted" style={{ fontSize: 13 }}>{d.last_seen_at ? new Date(d.last_seen_at).toLocaleString('pl-PL') : '—'}</td>
            <td>{d.status === 'revoked'
              ? <span className="pill pill-neutral">Odłączone</span>
              : d.status === 'pending'
                ? <span className="pill pill-warn">Oczekuje na parowanie</span>
                : <span className="pill pill-ok"><Icon name="check" size={12} /> Aktywne</span>}</td>
            <td className="right"><div className="flex" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" title="Generuj kod parowania" onClick={() => genCodeFor(d)}><Icon name="key-round" size={14} /></button>
              <button className="btn btn-ghost btn-sm" title="Edytuj" onClick={() => setEdit(d)}><Icon name="pencil" size={14} /></button>
              {d.status !== 'revoked' && (
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger-text)' }} title="Odłącz" onClick={() => revoke(d.id)}><Icon name="unlink" size={14} /></button>
              )}
            </div></td>
          </tr>
        ))}</tbody>
      </table></div>
      {devices.length === 0 && <div className="card-pad muted" style={{ fontSize: 13 }}>Brak sparowanych urządzeń.</div>}
      {edit && (
        <Modal title="Edytuj urządzenie" icon="smartphone" onClose={() => setEdit(null)}
          footer={<>
            <div className="right" />
            <button className="btn btn-ghost" onClick={() => setEdit(null)}>Anuluj</button>
            <button className="btn btn-primary" disabled={!edit.name} onClick={saveEdit}><Icon name="save" size={16} /> Zapisz</button>
          </>}>
          <div className="wfield"><label>Nazwa</label><input value={edit.name || ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="np. pakownia-telefon-1" autoFocus /></div>
          <div className="wfield"><label>Stanowisko</label>
            <select value={edit.station_id || ''} onChange={(e) => setEdit({ ...edit, station_id: e.target.value || null })}>
              <option value="">— brak —</option>
              {stations.map((s) => <option key={s.station_id} value={s.station_id}>{s.name || s.station_id}</option>)}
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Firma (nadawca) ---------- */
function SenderTab() {
  const push = useAdminToast();
  const qc = useQueryClient();
  const { data } = useQuery<Sender>({
    queryKey: ['settings-sender'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/sender`)).data || {},
  });
  const [form, setForm] = useState<Sender>({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setForm(data); }, [data]);

  const set = (k: keyof Sender) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save() {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/settings/sender`, form);
      qc.invalidateQueries({ queryKey: ['settings-sender'] });
      qc.invalidateQueries({ queryKey: ['sender-config'] }); // hub mapy dostaw (useSenderHub)
      push('Dane nadawcy zapisane');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zapisu danych nadawcy', 'err'); }
    finally { setSaving(false); }
  }

  return (
    <div className="card">
      <div className="card-head">
        <div><h3>Dane firmy — nadawca</h3><div className="ch-sub">Używane jako adres nadawcy przesyłek. Miasto wyznacza hub na mapie dostaw.</div></div>
        <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}><Icon name="save" size={14} /> Zapisz</button>
      </div>
      <div className="card-pad" style={{ paddingTop: 8 }}>
        <div className="wfield"><label>Nazwa firmy</label><input value={form.name || ''} onChange={set('name')} placeholder="np. OGYY Sp. z o.o." /></div>
        <div className="wfield-row">
          <div className="wfield"><label>Imię</label><input value={form.firstName || ''} onChange={set('firstName')} /></div>
          <div className="wfield"><label>Nazwisko</label><input value={form.lastName || ''} onChange={set('lastName')} /></div>
        </div>
        <div className="wfield-row">
          <div className="wfield"><label>E-mail</label><input value={form.email || ''} onChange={set('email')} placeholder="kontakt@firma.pl" /></div>
          <div className="wfield"><label>Telefon</label><input value={form.phone || ''} onChange={set('phone')} placeholder="600 100 200" /></div>
        </div>
        <div className="wfield-row">
          <div className="wfield"><label>Ulica</label><input value={form.street || ''} onChange={set('street')} /></div>
          <div className="wfield"><label>Nr budynku</label><input value={form.building || ''} onChange={set('building')} /></div>
        </div>
        <div className="wfield-row">
          <div className="wfield"><label>Miasto</label><input value={form.city || ''} onChange={set('city')} /></div>
          <div className="wfield"><label>Kod pocztowy</label><input value={form.postalCode || ''} onChange={set('postalCode')} placeholder="00-000" /></div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Panel pakowania (GET/POST /api/settings/station) ---------- */
type StationConfig = { unidentifiedFirst: boolean; autoLogoutEnabled: boolean; autoLogoutMinutes: number };

function StationPanelTab() {
  const push = useAdminToast();
  const qc = useQueryClient();
  const { data } = useQuery<StationConfig>({
    queryKey: ['station-config'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/station`)).data,
  });
  const [minutes, setMinutes] = useState('');
  useEffect(() => { if (data) setMinutes(String(data.autoLogoutMinutes)); }, [data]);

  async function save(patch: Partial<StationConfig>) {
    try {
      const r = await axios.post(`${API_URL}/api/settings/station`, patch);
      qc.setQueryData(['station-config'], r.data);
      push('Ustawienia panelu pakowania zapisane');
    } catch (e: any) {
      push(e?.response?.data?.error?.message || e?.response?.data?.error || 'Błąd zapisu ustawień', 'err');
      qc.invalidateQueries({ queryKey: ['station-config'] });
    }
  }
  function saveMinutes() {
    const n = Math.round(Number(minutes));
    if (!Number.isFinite(n) || n < 1 || n > 720) { push('Czas bezczynności: od 1 do 720 minut', 'err'); return; }
    if (n !== data?.autoLogoutMinutes) void save({ autoLogoutMinutes: n });
  }

  if (!data) return <PageSkeleton />;
  return (
    <div className="card">
      <div className="card-head">
        <div><h3>Panel pakowania</h3><div className="ch-sub">Zachowanie stacji pakowania (kiosk, aplikacja desktop i mobilna)</div></div>
      </div>
      <div className="card-pad" style={{ paddingTop: 8 }}>
        <div className="set-row">
          <div className="meta">
            <div className="t">Nierozpoznana metoda dostawy na samej górze</div>
            <div className="d">Zamówienia, których UCS nie potrafi przypisać do kuriera, pojawiają się na początku listy — żeby operator od razu zgłosił je administratorowi. Kolejność kategorii bez zmian („Inne” na końcu).</div>
          </div>
          <Toggle on={data.unidentifiedFirst} onChange={(v) => save({ unidentifiedFirst: v })} />
        </div>
        <div className="set-row">
          <div className="meta">
            <div className="t">Automatyczne wylogowanie operatora</div>
            <div className="d">Po ustalonym czasie bez dotyku, klawiatury i skanu stacja wraca do logowania PIN-em. Nigdy w trakcie otwartej sesji pakowania.</div>
          </div>
          <Toggle on={data.autoLogoutEnabled} onChange={(v) => save({ autoLogoutEnabled: v })} />
        </div>
        <div className="set-row" style={{ opacity: data.autoLogoutEnabled ? 1 : 0.5 }}>
          <div className="meta"><div className="t">Czas bezczynności (min)</div><div className="d">Od 1 do 720 minut</div></div>
          <input className="select" style={{ width: 80 }} type="number" min={1} max={720}
            disabled={!data.autoLogoutEnabled}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            onBlur={saveMinutes}
            onKeyDown={(e) => { if (e.key === 'Enter') saveMinutes(); }} />
        </div>
      </div>
    </div>
  );
}

/* ---------- UCS: jedyna integracja v2 (konfiguracja + status) ---------- */
function UcsPointer() {
  const push = useAdminToast();
  const qc = useQueryClient();
  const { orgSlug, displayName } = useOrg();
  const { data: ucs } = useQuery<any>({
    queryKey: ['ucs-status'],
    queryFn: async () => {
      try { return (await axios.get(`${API_URL}/api/settings/ucs/status`)).data; }
      catch { return { configured: false, connected: false }; }
    },
    refetchInterval: 60000,
  });
  const { data: cfg } = useQuery<any>({
    queryKey: ['ucs-config'],
    queryFn: async () => {
      try { return (await axios.get(`${API_URL}/api/settings/ucs`)).data; }
      catch { return null; }
    },
  });
  const [edit, setEdit] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');

  useEffect(() => { if (cfg?.baseUrl) setBaseUrl(cfg.baseUrl); }, [cfg]);

  async function save() {
    try {
      const body: any = {};
      if (baseUrl) body.baseUrl = baseUrl;
      if (apiKey) body.apiKey = apiKey;
      await axios.post(`${API_URL}/api/settings/ucs`, body);
      setApiKey('');
      setEdit(false);
      qc.invalidateQueries({ queryKey: ['ucs-status'] });
      qc.invalidateQueries({ queryKey: ['ucs-config'] });
      push('Ustawienia UCS zapisane');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zapisu ustawień UCS', 'err'); }
  }

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div className="card-pad" style={{ display: 'flex', gap: 16, alignItems: 'flex-start', background: 'var(--accent-soft)', borderBottom: '1px solid var(--divider)' }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--accent)', color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name="orbit" size={22} /></div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Universal Carrier Service — jedyna integracja PackFlow</div>
          <div style={{ fontSize: 13, color: 'var(--fg-2)', marginTop: 4, maxWidth: '62ch' }}>
            Zamówienia, etykiety kurierskie, fulfillment i tracking pochodzą z UCS. Połączenia z kurierami
            i Shopify konfigurujesz centralnie w UCS — PackFlow potrzebuje tylko adresu i klucza API tenanta.
          </div>
        </div>
        <div className="flex" style={{ flexShrink: 0, gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => setEdit((v) => !v)}><Icon name="pencil" size={15} /> Konfiguruj</button>
          {ucs?.baseUrl ? (
            <a className="btn btn-primary" style={{ textDecoration: 'none' }} href={ucs.baseUrl} target="_blank" rel="noreferrer"><Icon name="external-link" size={15} /> Otwórz UCS</a>
          ) : (
            <button className="btn btn-primary" disabled><Icon name="external-link" size={15} /> Otwórz UCS</button>
          )}
        </div>
      </div>
      {edit && (
        <div className="card-pad" style={{ borderBottom: '1px solid var(--divider)' }}>
          <div className="wfield-row">
            <div className="wfield"><label>Adres UCS (baseUrl)</label><input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://ucs.insolitum.ai" /></div>
            <div className="wfield"><label>Klucz API {cfg?.apiKeyMasked ? `(obecny: ${cfg.apiKeyMasked})` : ''}</label>
              <input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="ucs_live_… (puste = bez zmian)" /></div>
          </div>
          <button className="btn btn-primary" onClick={save}><Icon name="save" size={16} /> Zapisz</button>
        </div>
      )}
      <div className="card-pad" style={{ paddingTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--fg-3)', marginBottom: 10 }}>
          Stan integracji — organizacja {displayName || orgSlug || ''}
        </div>
        <div className="muted" style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name={ucs?.connected ? 'check-circle-2' : 'alert-circle'} size={14} />
          {ucs?.configured
            ? ucs?.connected
              ? <>połączony z <span className="mono">{ucs.baseUrl}</span>{ucs?.me?.environment ? ` · środowisko ${ucs.me.environment}` : ''}{ucs?.me?.scopes ? ` · scope: ${ucs.me.scopes.join(', ')}` : ''}</>
              : `skonfigurowany · brak połączenia${ucs?.error ? ` (${ucs.error})` : ''}`
            : 'nieskonfigurowany — podaj adres UCS i klucz API'}
        </div>
      </div>
    </div>
  );
}

/* ---------- strona ---------- */
export default function SettingsPage() {
  // Zakładkę można otworzyć linkiem: /settings?tab=magazyn (np. z Magazynu).
  const [params, setParams] = useSearchParams();
  const [tab, setTabState] = useState(() => params.get('tab') || 'printers');
  const urlTab = params.get('tab');
  useEffect(() => { if (urlTab) setTabState(urlTab); }, [urlTab]);
  const setTab = (id: string) => { setTabState(id); setParams(id === 'printers' ? {} : { tab: id }, { replace: true }); };
  const { orgSlug, displayName } = useOrg();
  const { isLoading } = useQuery({
    queryKey: ['settings-printers'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/printers`)).data,
  });

  if (isLoading) return <PageSkeleton table />;

  const tabs: [string, string, string][] = [
    ['printers', 'Drukarki', 'printer'],
    ['packages', 'Typy paczek', 'box'],
    ['operators', 'Operatorzy', 'users'],
    ['stations', 'Stacje', 'cpu'],
    ['station-panel', 'Panel pakowania', 'package-open'],
    ['devices', 'Urządzenia mobilne', 'smartphone'],
    ['sender', 'Firma', 'building-2'],
    ['magazyn', 'Magazyn', 'warehouse'],
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div><h1>Ustawienia</h1><div className="ph-sub">Konfiguracja modułu PackFlow dla organizacji {displayName || orgSlug || ''}</div></div>
      </div>
      <div className="tabs">
        {tabs.map(([id, lbl, ic]) => (
          <button key={id} className={`tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}><Icon name={ic} size={15} /> {lbl}</button>
        ))}
      </div>
      {tab === 'printers' && <PrintersTab />}
      {tab === 'packages' && <PackagesTab />}
      {tab === 'operators' && <OperatorsTab />}
      {tab === 'stations' && <StationsTabSettings />}
      {tab === 'station-panel' && <StationPanelTab />}
      {tab === 'devices' && <DevicesTab />}
      {tab === 'sender' && <SenderTab />}
      {tab === 'magazyn' && <StockSettingsTab />}
      <div style={{ marginTop: 20 }}><UcsPointer /></div>
    </div>
  );
}
